import type { EChartsOption, SeriesOption, XAXisComponentOption, YAXisComponentOption } from 'echarts';

import type { TimeSeriesChartProps } from '@/shared/charts/time-series-chart/TimeSeriesChart.types';
import { getHighlightedLanes } from '@/shared/charts/time-series-chart/model/getHighlightedLanes';
import type { TimeSeriesChartTheme } from '@/shared/charts/time-series-chart/theme/TimeSeriesChartTheme.types';

import { PLOT_X_AXIS_INDEX, TRACK_X_AXIS_INDEX } from './config/chartLayout';
import { animationVisuals } from './config/chartVisuals';
import { buildGridOption } from './layout/buildGridOption';
import { buildSectionLabelOption, getVisibleSectionLabels } from './layout/buildSectionLabelOption';
import { buildNavigationOption } from './navigation/buildNavigationOption';
import { buildPlotOption } from './plot/buildPlotOption';
import type { LineViewport } from './plot/level-of-detail/lineViewport';
import { buildRangeTrackOption } from './range-track/buildRangeTrackOption';
import { buildTooltipAnchorOption } from './tooltip/options/buildTooltipAnchorOption';
import { buildTooltipOption } from './tooltip/options/buildTooltipOption';
import { toTimeDomain } from './utils/timeDomain';

export type BuildChartOptionParams = Omit<TimeSeriesChartProps, 'accessibility'> & {
  theme: TimeSeriesChartTheme;
  renderTooltipContent: () => string;
  /** The settled time window and plot width the lines are drawn for (see LineViewport). */
  viewport: LineViewport;
};

export function buildChartOption({
  series,
  rangeTrack,
  timeDomain,
  highlight,
  navigation,
  isTooltipEnabled = false,
  sectionLabels,
  theme,
  renderTooltipContent,
  viewport,
}: BuildChartOptionParams): EChartsOption {
  const highlightedLanes = getHighlightedLanes(rangeTrack, highlight?.laneIds);
  const laneCount = rangeTrack?.lanes.length ?? 0;
  const hasNavigation = navigation !== undefined;
  const { plotLabel, rangeTrackLabel } = getVisibleSectionLabels(sectionLabels, laneCount);
  const domain = toTimeDomain(timeDomain);

  const plot = buildPlotOption({
    series,
    highlightedLanes,
    highlight,
    domain,
    isGuideLineEnabled: isTooltipEnabled,
    viewport,
    previewSeriesId: navigation?.previewSeriesId,
    hasSliderPreview: hasNavigation,
    theme,
  });

  const track =
    rangeTrack && laneCount > 0
      ? buildRangeTrackOption({
          rangeTrack,
          highlightedLanes,
          domain,
          yAxisIndex: plot.yAxis.length,
          theme,
        })
      : undefined;

  const xAxis: XAXisComponentOption[] = track ? [plot.xAxis, track.xAxis] : [plot.xAxis];
  const yAxis: YAXisComponentOption[] = track ? [...plot.yAxis, track.yAxis] : plot.yAxis;
  const allSeries: SeriesOption[] = track ? [...plot.series, ...track.series] : plot.series;

  // The navigable axes: the anchor axis added below must stay outside the data zoom.
  const navigableXAxisIndexes = track ? [PLOT_X_AXIS_INDEX, TRACK_X_AXIS_INDEX] : [PLOT_X_AXIS_INDEX];

  if (isTooltipEnabled) {
    const anchor = buildTooltipAnchorOption({ xAxisIndex: xAxis.length, yAxisIndex: yAxis.length });

    xAxis.push(anchor.xAxis);
    yAxis.push(anchor.yAxis);
    allSeries.push(anchor.series);
  }

  return {
    animation: true,
    animationDuration: animationVisuals.initialDurationMs,
    animationDurationUpdate: animationVisuals.updateDurationMs,

    grid: buildGridOption(laneCount, hasNavigation, rangeTrackLabel !== undefined),
    graphic: buildSectionLabelOption({ plotLabel, rangeTrackLabel, laneCount, hasNavigation, theme }),

    xAxis,
    yAxis,
    series: allSeries,

    // The guide line must move on both time axes at once, otherwise it only appears in the plot.
    ...(isTooltipEnabled &&
      track && {
        axisPointer: {
          link: [{ xAxisIndex: navigableXAxisIndexes }],
        },
      }),

    ...(hasNavigation && buildNavigationOption({ xAxisIndexes: navigableXAxisIndexes, theme })),
    ...(isTooltipEnabled && buildTooltipOption(renderTooltipContent)),
  };
}
